import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "../../hooks";
import { WellProps } from "../../interfaces/interfaces";
import { AlertComponent, NavBar, ShowContent, Spinner } from "../../components";
import EditIcon from "@mui/icons-material/Edit";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { IconButton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DataContext from "../../context/DataContext";
import { useRequest } from "../../hooks";
import {
  modernDataGridStyles,
  visibleIconButtonStyles,
} from "../../utils/dataGridStyles";

interface Props {
  id: number;
  name: string;
  wells: WellProps[];
  active: boolean;
  updatedAt: string;
}

interface FetchResponse {
  success?: boolean;
  data: Props;
}

export const ClientDetail = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { idClient } = useParams();
  const theme = useTheme();
  const matches = useMediaQuery("(min-width:600px)");
  const { data, error, isLoading } = useFetch<FetchResponse>(
    `client-detail/${idClient}`
  );
  const [clientName, setClientName] = useState("");
  const [clientStatus, setClientStatus] = useState("Active");
  const [clientUsers, setClientUsers] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const { isSuccess, isError, onEditClient } = useContext(DataContext);

  useEffect(() => {
    if (data) {
      const {
        data: { name, active },
      } = data;
      setClientName(name);
      active ? setClientStatus("Active") : setClientStatus("Inactive");
    }
  }, [data]);

  const fetchClientUsers = () => {
    if (!idClient) return;
    const options: RequestInit = { method: "GET" };
    Promise.all([
      handleRequest({ endpoint: "user", options }),
      handleRequest({ endpoint: `user/client/${idClient}`, options }),
    ]).then(([usersRes, assignedRes]) => {
      const assigned = assignedRes.success ? assignedRes.data : [];
      setClientUsers(assigned);
      const assignedIds = new Set(assigned.map((user: any) => user.id));
      setUserOptions(
        (usersRes.success ? usersRes.data : []).filter(
          (user: any) =>
            user.active && user.idRol === 3 && !assignedIds.has(user.id),
        ),
      );
    });
  };

  useEffect(() => {
    fetchClientUsers();
  }, [idClient]);

  const assignUser = () => {
    if (!selectedUser || !idClient) return;
    handleRequest({
      endpoint: "user-client",
      options: {
        method: "POST",
        body: JSON.stringify({
          userId: selectedUser.id,
          clientId: parseInt(idClient),
        }),
      },
    }).then(() => {
      setSelectedUser(null);
      fetchClientUsers();
    });
  };

  const removeUser = (userId: number) => {
    if (!idClient) return;
    handleRequest({
      endpoint: `user-client/${userId}/${idClient}`,
      options: { method: "DELETE" },
    }).then(() => fetchClientUsers());
  };

  const buttons = [
    {
      title: "Edit",
      action: () =>
        idClient &&
        onEditClient(
          parseInt(idClient),
          clientName,
          clientStatus === "Active" ? true : false
        ),
      icon: <EditIcon />,
      disabled: !idClient,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "detail",
      headerName: "Action",
      renderHeader: () => <strong>{"DETAIL"}</strong>,
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Tooltip title="Open Well">
          <IconButton
            onClick={() => navigate(`/well/${params.row.id}`)}
            sx={visibleIconButtonStyles(theme)}
            size="small"
            aria-label="Open well detail"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Well",
      renderHeader: () => <strong>{"WELL"}</strong>,
      sortable: true,
      width: 350,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.name || "",
      renderCell: (params: any) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/well/${params.row.id}`)}
        >
          {params.row?.name || ""}
        </div>
      ),
    },
    {
      field: "contact",
      headerName: "Contact",
      renderHeader: () => <strong>{"CONTACT"}</strong>,
      width: 220,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      renderHeader: () => <strong>{"PHONE NUMBER"}</strong>,
      width: 220,
    },
  ];

  let content: JSX.Element | JSX.Element[];

  if (!data) {
    content = <Spinner />;
  } else {
    const {
      data: { wells },
    } = data;
    content = (
      <>
        <NavBar title={clientName} buttons={buttons} />
        <Box
          sx={{
            marginTop: "80px",
            minHeight: "calc(100vh - 80px)",
            background:
              "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
            padding: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Fade in={true} timeout={600}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 3 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "rgb(140, 150, 169)",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 1,
                    }}
                  >
                    Client Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item lg={6} xs={12}>
                      <TextField
                        label="Client Name"
                        variant="outlined"
                        value={clientName}
                        fullWidth
                        onChange={(e) => setClientName(e.target.value)}
                      />
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Autocomplete
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        )}
                        value={clientStatus}
                        onChange={(_, newValue) =>
                          (newValue === "Active" || newValue === "Inactive") &&
                          setClientStatus(newValue)
                        }
                        options={["Active", "Inactive"]}
                        disablePortal
                        fullWidth
                        renderInput={(params) => (
                          <TextField {...params} label="Status" />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 3 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "rgb(140, 150, 169)",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 1,
                    }}
                  >
                    Assigned Users
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} (${option.email})`
                            : ""
                        }
                        onChange={(_, value) => setSelectedUser(value)}
                        options={userOptions}
                        value={selectedUser}
                        renderInput={(params) => (
                          <TextField {...params} label="Client user" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        disabled={!selectedUser}
                        onClick={assignUser}
                        variant="contained"
                      >
                        Assign user
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {clientUsers.map((user) => (
                          <Chip
                            key={user.id}
                            label={`${user.name} (${user.email})`}
                            onDelete={() => removeUser(user.id)}
                          />
                        ))}
                        {!clientUsers.length && (
                          <Typography color="text.secondary" variant="body2">
                            No client users assigned.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 4 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <Box sx={{ height: "60vh", width: "100%" }}>
                    <DataGrid
                      slots={{ toolbar: GridToolbar }}
                      density={matches ? "standard" : "compact"}
                      sx={modernDataGridStyles(theme)}
                      rows={wells}
                      columns={columns}
                      autoPageSize
                    />
                  </Box>
                </Paper>
              </Box>
            </Fade>
          </Container>
        </Box>
        {isSuccess && <AlertComponent type="success" />}
        {isError && <AlertComponent type="error" />}
      </>
    );
  }

  return <ShowContent error={error} isLoading={isLoading} content={content} />;
};
